import { IonContent } from "@ionic/react";
import { Markdown } from "../components/Markdown";
import styled from "styled-components";
import { useTranslate } from "../contexts/translate";
import { Background } from "../components/ui/Background";
import { Subheader } from "../components/ui/Subheader";
import { BigButton } from "../components/ui/BigButton";
import { PaperBackground } from "../components/ui/PaperBackground";
import { Header } from "../components/ui/Header";

const md = {
  fr: `
  ## Comment lire une carte résumé
  ![IC](Howto/howtoread.png)

  **1- Type de carte résumé et numéro de référence.** La plupart des règles spéciales utilisées dans un scénario sont appelées par leur titre, accompagné de leur type et de leur numéro de référence (par exemple : “Montagne - Terrain 30”, “Cavalerie - Troupes 8”...)
  
  **2-** (Seulement pour les cartes terrain) **Règles spéciales récurrentes** qui s’appliquent à ce type de terrain, indiquées par les icônes suivantes :

  ![IC](Terrains Cards/Icones/stop.png)
  Une unité qui se déplace sur ce type de terrain doit s’arrêter et ne peut plus se déplacer pour le reste du tour.
  
  ![IC](Terrains Cards/Icones/noatt.png)
  Une unité qui se déplace sur ce type de terrain ne peut pas attaquer lors du même tour.
  
  ![IC](Terrains Cards/Icones/los.png)
  Ce type de terrain bloque la ligne de vue.
  
  ![IC](Terrains Cards/Icones/flag.png)
  Une unité située sur ce type de terrain peut ignorer 1 drapeau chaque fois qu’elle est attaquée (même si elle est attaquée plusieurs fois dans le même tour).
  
  **3- Règles spéciales additionnelles**, s’il y en a.
  
  **4-** (Seulement pour les cartes terrain) **Protections de terrain.** Ces icônes indiquent la réduction du nombre de dés d’attaque qui s’appliquent à certains types d’unités quand leur cible se trouve sur ce type de terrain. 
  ![def](Terrains Cards/Def/inf-1.png) ![def](Terrains Cards/Def/arm-2.png)
  Dans cet exemple, l’infanterie lancera 1 dé de moins, et les blindés 2 dés de moins, au moment d’attaquer une unité qui se trouve sur un hex de bocage.
  
  **Note :** il arrive parfois que les icônes de règles récurrentes et de protection de terrains affichent un ou deux astérisques.
  ![def](Terrains Cards/Def/inf-2_b.png) ![IC](Terrains Cards/Icones/los_c.png)
  Cela signifie que ces règles s’appliquent, mais sous certaines conditions, ou avec certaines modifications, qui sont spécifiées dans la section des règles spéciales additionnelles de la carte.
`,

  en: `
  ## How to read a summary card
  ![IC](Howto/howtoread.png)

  **1- Summary card type and reference number.** Most of the special rules used in a scenario briefing are summed up using their name, as well as their type and reference number (e.g. Mountain - Terrain 30, Cavalry - Troops 8...).
  
  **2-** (On terrain summary cards only) **Frequent special rules** that apply to terrain, indicated by the following icons:

  ![IC](Terrains Cards/Icones/stop.png)
  A unit that enters this terrain hex must stop and may move no further on that turn.
  
  ![IC](Terrains Cards/Icones/noatt.png)
  A unit that enters this terrain hex cannot attack that turn.
  
  ![IC](Terrains Cards/Icones/los.png)
  This terrain hex blocks line of sight.
  
  ![IC](Terrains Cards/Icones/flag.png)
  A unit on this terrain hex may ignore 1 flag each time they are the target of an attack, even on the same turn.
  
  **3- Additional special rules**, if any.
  
  **4-** (On terrain summary cards only) **Terrain protections.** This indicates the attack dice reductions for the pictured unit types when they attack an enemy unit standing on this terrain. 
  ![def](Terrains Cards/Def/inf-1.png) ![def](Terrains Cards/Def/arm-2.png)
  In this example, infantry roll 1 less die and armor roll 2 less dice when attacking a unit standing on a hedgerow hex.
  
  **Note:** Sometimes, frequent special rules and terrain protection icons may be marked by one or two asterisks.
  ![def](Terrains Cards/Def/inf-2_b.png) ![IC](Terrains Cards/Icones/los_c.png)
  This means the rules apply, but under certain conditions, or with some amendments, which are detailed in the additional special rules section of the card.
`,
};

const Container = styled.div`
  width: 100%;
  padding: calc(var(--u) * 3);
  color: black;
  max-width: 600px;

  img {
    height: 32px;
  }

  img[src$="howtoread.png"] {
    height: auto;
  }

  line-height: 24px;
`;

const Help: React.FC = () => {
  const { translate, lang } = useTranslate();

  return (
    <>
      <Header size="small" shouldHide />
      <IonContent>
        <Background background="cards">
          <Subheader>
            <BigButton
              icon="cards"
              label={translate("screens.help.title")}
              to=""
              mode="title"
            />
          </Subheader>
          <IonContent style={{ "--background": "transparent" }}>
            <PaperBackground>
              <Container>
                <Markdown md={lang && md[lang] ? md[lang] : md.fr} />
              </Container>
            </PaperBackground>
          </IonContent>
        </Background>
      </IonContent>
    </>
  );
};

export default Help;
